import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Basemap, BasemapId } from '../lib/types';
import { useBasemapSelection } from '../hooks/useBasemapSelection';

interface BasemapContextValue {
  basemap: Basemap;
  basemapId: BasemapId;
  setBasemapId: (id: BasemapId) => void;
  isPickerOpen: boolean;
  openPicker: () => void;
  closePicker: () => void;
}

const BasemapContext = createContext<BasemapContextValue | null>(null);

export const BasemapProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { basemap, basemapId, setBasemapId } = useBasemapSelection();
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const openPicker = useCallback(() => setIsPickerOpen(true), []);
  const closePicker = useCallback(() => setIsPickerOpen(false), []);

  const value = useMemo<BasemapContextValue>(
    () => ({ basemap, basemapId, setBasemapId, isPickerOpen, openPicker, closePicker }),
    [basemap, basemapId, setBasemapId, isPickerOpen, openPicker, closePicker],
  );

  return <BasemapContext.Provider value={value}>{children}</BasemapContext.Provider>;
};

export const useBasemap = (): BasemapContextValue => {
  const ctx = useContext(BasemapContext);
  if (!ctx) {
    throw new Error('useBasemap must be used within a BasemapProvider');
  }
  return ctx;
};
